/* eslint-disable array-bracket-newline */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAccount, useNetwork } from 'wagmi';
import { IUser, IUserBoard } from '@kattana-io/common';
import Image from '../../../atoms/Image';
import Text from '../../../atoms/Text';
// import Highlight from './Highlight';
import { authTokenSelector } from '../../../selectors/auth';
import { IStore } from '../../../reducers';
import { toFixed2, toFixed4 } from '../../../components/DexTrades/formatters';
import useTokenBalance from '../../../hooks/useTokenBalance';
import { isEVMAddress } from '../../../helpers/checksumAddress';
import Loader from '../../../components/common/Loader';
import { ReactComponent as UserSkeleton } from '../../../assets/images/user_skeleton.svg';
import { actionSetCompetitionWallet } from '../../../actions/competition';
import { CexAPI, UserAPI } from '../../../store/createStore';
import { actionShowModal } from '../../../actions/modal';
import { ModalTypes } from '../../../reducers/modal';
import {
  AVATAR_PLACEHOLDER,
  KTN_TOKEN_ADDRESS,
} from '../../../helpers/constants';

import './styles.scss';

const getStatistics = (
  data: IUserBoard | null,
  ktnBalance: string
): Record<string, string | number> => {
  const pnl = data?.PNL || 0;
  return {
    PnL: `${(pnl <= 0 ? '' : '+') + toFixed2(pnl)}%`,
    'Total earned points:': toFixed2(data?.TotalEarnedPoints || 0),
    '24h volume:': '$' + toFixed4(data?.Volume24 || 0),
    'avg. 24h volume:': '$' + toFixed4(data?.AvgVolume24 || 0),
    'Total trades:': data?.TotalTrades || 0,
    // 'Paid fees:': '$645.25',
    'Favourite trading pair:': data?.FavoriteTradingPair || '-',
    'KTN Balance:': ktnBalance,
  };
};

// const getHighlights = (): any => [
//   {
//     title: 'Multiplier of the day',
//     link: '/competition/binance',
//     description: {
//       Binance: { value: '$1.25', type: 'text' },
//       'KTN/USDT': {
//         value: 0.15,
//         type: 'change',
//       },
//     },
//   },
//   {
//     title: 'New pair',
//     link: '/competition/binance',
//     description: {
//       Binance: { value: '$2.34', type: 'text' },
//       'KTN/USDT': {
//         value: -0.23,
//         type: 'change',
//       },
//     },
//   },
//   {
//     title: 'Most points a day',
//     link: '/competition/binance',
//     description: {
//       '#23': { value: 'Super trader', type: 'badge' },
//       Added: {
//         value: '+25000 points',
//         type: 'change',
//       },
//     },
//   },
// ];

const Profile = () => {
  const authToken = useSelector(authTokenSelector);
  const active = useSelector((state: IStore) => state.competition.wallet);
  const refresh = useSelector((state: IStore) => state.competition.refresh);
  const leaderboard = useSelector(
    (state: IStore) => state.competition.leaderboard
  );

  const { address: currentUser } = useAccount();
  const { chain } = useNetwork();
  const { exchange } = useParams();
  const dispatch = useDispatch();

  const [userBoard, setUserBoard] = useState<IUserBoard | null>(null);
  const [user, setUser] = useState<IUser | null>(null);
  const [boardLoading, setBoardLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(true);

  const internalNetwork = chain?.network;
  let network = 'EVM';
  if (internalNetwork == 'ELROND') {
    network = 'ELROND';
  } else if (internalNetwork == 'TRON') {
    network = 'TRON';
  } else if (internalNetwork == 'TON') {
    network = 'TON';
  }

  const activeUser = active || currentUser;

  const ktnBalance = useTokenBalance({
    address: activeUser,
    chainId: 1,
    token: KTN_TOKEN_ADDRESS,
    enabled: isEVMAddress(activeUser || ''),
  });

  useEffect(() => {
    const controller = new AbortController();

    setUserLoading(true);
    setBoardLoading(true);
    if (activeUser) {
      // Pass address
      CexAPI.fetchUserBoard(
        exchange || 'binance',
        network,
        activeUser,
        controller
      ).then((data) => {
        setUserBoard(data);
        setBoardLoading(false);
      });

      UserAPI.fetchUser(activeUser, network, controller).then((data) => {
        setUser(data);
        setUserLoading(false);
      });
    } else {
      setUserBoard(null);
      setUserLoading(false);
      setBoardLoading(false);
    }

    return () => {
      controller.abort();
    };
  }, [activeUser, exchange, network, refresh]);

  const onEdit = () => {
    if (user && network && authToken) {
      dispatch(
        actionShowModal(ModalTypes.editProfile, { user, network, authToken })
      );
    }
  };

  const onChangeAvatar = () => {
    if (user && network && authToken) {
      dispatch(
        actionShowModal(ModalTypes.changeAvatar, { network, authToken })
      );
    }
  };

  const onReturnToOwnAccount = () => {
    dispatch(actionSetCompetitionWallet(undefined));
  };

  const statistics = getStatistics(userBoard, ktnBalance.toFixed(4));
  const rank =
    leaderboard.findIndex((item) => item.wallet === activeUser) + 1 ||
    'No rank';

  // const highlights = getHighlights();

  return (
    <div className='profile'>
      <div className='profile__user'>
        {activeUser !== currentUser && (
          <div
            className='profile__user__to_own icon-corner-up-left'
            onClick={onReturnToOwnAccount}
          />
        )}
        <div className='profile__user__header'>
          {userLoading || !activeUser ? (
            <div className='profile__user__header__skeleton'>
              <UserSkeleton />
            </div>
          ) : (
            <>
              <div
                className='profile__user__header__avatar'
                onClick={onChangeAvatar}
              >
                <Image
                  size='avatar'
                  src={user?.img_url || AVATAR_PLACEHOLDER}
                />
              </div>
              <div className='profile__user__header__info'>
                <div className='profile__user__header__info__nickname'>
                  <Text
                    size={20}
                    className='profile__user__header__info__nickname__field'
                  >
                    {user?.nickname || ''}
                  </Text>
                  {activeUser === currentUser && (
                    <div
                      className='profile__user__header__info__nickname__icon icon-edit'
                      onClick={onEdit}
                    />
                  )}
                </div>
                <div className='profile__user__header__info__curr_rank'>
                  <Text size={20} className='text--grey-200'>
                    {rank}
                  </Text>
                  <div className='profile__user__header__info__curr_rank__icon icon-dots-arrow-up' />
                </div>
                <Text size={12} className='text--grey-600'>
                  Highest rank #{rank}
                </Text>
              </div>
            </>
          )}
        </div>

        <div className='profile__user__statistics'>
          <div className='profile__user__statistics__stat'>
            <Text size={12}>Social</Text>
            <div className='profile__user__statistics__stat__socials'>
              <a
                href={user?.twitter_url}
                className={`icon-twitter ${
                  !user?.github_url
                    ? 'profile__user__statistics__stat__socials--inactive'
                    : ''
                }`}
                target='_blank'
                rel='noreferrer'
              />
              <a
                href={user?.facebook_url}
                className={`icon-facebook ${
                  !user?.github_url
                    ? 'profile__user__statistics__stat__socials--inactive'
                    : ''
                }`}
                target='_blank'
                rel='noreferrer'
              />
              <a
                href={user?.github_url}
                className={`icon-github ${
                  !user?.github_url
                    ? 'profile__user__statistics__stat__socials--inactive'
                    : ''
                }`}
                target='_blank'
                rel='noreferrer'
              />
            </div>
          </div>
          {boardLoading ? (
            <div className='centrify'>
              <Loader />
            </div>
          ) : (
            <>
              {Object.keys(statistics).map((key) => (
                <div key={key} className='profile__user__statistics__stat'>
                  <Text size={12}>{key}</Text>
                  <Text size={12}>{statistics[key]}</Text>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      {/* <div className='profile__highlights'>
        <Text size={14} className='profile__highlights__header'>
          Competition Highlights
        </Text>
        {highlights.map((item: any) => (
          <div key={item.title} className='profile__highlights__item'>
            <div className='profile__highlights__item__title'>
              <Text size={14} className='text--grey-200'>
                {item.title}
              </Text>
              <a
                href={item.link}
                className='select_wallet__body__active__content__description__wallet__actions__icon icon-top-right-square'
              />
            </div>
            <div className='profile__highlights__item__content'>
              {Object.keys(item.description).map((highlight) => (
                <div
                  key={highlight}
                  className='profile__highlights__item__content__description'
                >
                  <Text size={12}>{highlight}</Text>
                  <Highlight
                    type={item.description[highlight].type}
                    value={item.description[highlight].value}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div> */}
    </div>
  );
};

export default Profile;
